import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as itemService from '../../services/itemService';
import { Item, ItemFormValues } from '../../types/item';

interface InventoryState {
  items: Item[];
  categories: string[];
  selectedItem: Item | null;
  loading: boolean;
  error: string | null;
}

const initialState: InventoryState = {
  items: [],
  categories: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const fetchItemsAsync = createAsyncThunk(
  'inventory/fetchItems',
  async (params?: {
    category?: string;
    search?: string;
    has_warranty?: boolean;
    page?: number;
    page_size?: number;
  }) => {
    const response = await itemService.getItems(params);
    return response;
  }
);

export const fetchCategoriesAsync = createAsyncThunk(
  'inventory/fetchCategories',
  async () => {
    const response = await itemService.getCategories();
    return response;
  }
);

export const addItemAsync = createAsyncThunk(
  'inventory/addItem',
  async (item: ItemFormValues) => {
    const response = await itemService.createItem(item);
    return response;
  }
);

export const updateItemAsync = createAsyncThunk(
  'inventory/updateItem',
  async ({ id, item }: { id: number; item: Partial<ItemFormValues> }) => {
    const response = await itemService.updateItem(id, item);
    return response;
  }
);

export const deleteItemAsync = createAsyncThunk(
  'inventory/deleteItem',
  async (id: number) => {
    await itemService.deleteItem(id);
    return id;
  }
);

export const getItemByIdAsync = createAsyncThunk(
  'inventory/getItemById',
  async (id: number) => {
    const response = await itemService.getItemById(id);
    return response;
  }
);

export const getItemByBarcodeAsync = createAsyncThunk(
  'inventory/getItemByBarcode',
  async (barcode: string) => {
    const response = await itemService.getItemByBarcode(barcode);
    return response;
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearSelectedItem(state) {
      state.selectedItem = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Items
      .addCase(fetchItemsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchItemsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch items';
      })
      // Fetch Categories
      .addCase(fetchCategoriesAsync.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      // Add Item
      .addCase(addItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(addItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add item';
      })
      // Update Item
      .addCase(updateItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update item';
      })
      // Delete Item
      .addCase(deleteItemAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      // Get Item by ID
      .addCase(getItemByIdAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getItemByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedItem = action.payload;
      })
      .addCase(getItemByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Item not found';
      })
      // Get Item by Barcode
      .addCase(getItemByBarcodeAsync.fulfilled, (state, action) => {
        state.selectedItem = action.payload;
      });
  },
});

export const { clearSelectedItem, clearError } = inventorySlice.actions;

// Selectors
export const selectItems = (state: any) => state.inventory.items;
export const selectCategories = (state: any) => state.inventory.categories;
export const selectSelectedItem = (state: any) => state.inventory.selectedItem;
export const selectLoading = (state: any) => state.inventory.loading;
export const selectError = (state: any) => state.inventory.error;

export default inventorySlice.reducer;